import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {Trash2} from 'lucide';
import {Button} from '~/components/Button';
import {TagsList} from '~/components/Controls/Tags';
import {useConfirm} from '~/components/Dialogs';
import {onSubmitError} from '~/components/Form';
import {LucideIcon} from '~/components/LucideIcon';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {Table, TableRowActions} from '~/components/Table';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {formatDateTime} from '~/lib/date';
import {getApiErrorMessage} from '~/lib/errors/apiError';
import {Intent} from '~/lib/intent';
import {ClientsAbility} from '~/modules/Clients/ability';
import {
    deleteSellerSensitiveCategory,
    fetchSellerSensitiveCategories,
    saveSellerSensitiveCategory
} from '~/modules/Clients/api';
import {ISensitiveCategoryFormProps, SensitiveCategoryForm} from '~/modules/Clients/forms/SensitiveCategoryForm';
import {
    getEditorLabel,
    getSensitiveCategoryDecisionLabel,
    ISensitiveCategoryDeclaration,
    SensitiveCategory,
    sensitiveCategories,
    SensitiveCategoryDecision,
    toDeclarationRequest
} from '~/modules/Clients/models/SensitiveCategory';
import style from './SellerSensitiveCategories.module.scss';

interface ISellerSensitiveCategoriesProps {
    sellerId: string;
}

interface ICategoryRow {
    category: SensitiveCategory;
    label: string;
    declaration?: ISensitiveCategoryDeclaration;
}

const statusClass = (decision?: SensitiveCategoryDecision) => {
    if (decision === SensitiveCategoryDecision.ALLOWED) {
        return style.statusAllowed;
    }
    if (decision === SensitiveCategoryDecision.NOT_ALLOWED) {
        return style.statusNotAllowed;
    }
    return style.statusNotAnswered;
};

export const SellerSensitiveCategories: FC<ISellerSensitiveCategoriesProps> = ({sellerId}) => {
    const ability = useAbility(ClientsAbility);
    const confirm = useConfirm();
    const modal = useModals();
    const [declarations, setDeclarations] = useState<ISensitiveCategoryDeclaration[]>([]);
    const [isPending, setPending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        setPending(true);
        setError(null);
        fetchSellerSensitiveCategories(sellerId)
            .then(setDeclarations)
            .catch(e => setError(getApiErrorMessage(e, 'Could not load sensitive categories')))
            .finally(() => setPending(false));
    }, [sellerId]);

    useEffect(load, [load]);

    const replace = (saved: ISensitiveCategoryDeclaration) =>
        setDeclarations(current => [...current.filter(d => d.category !== saved.category), saved]);

    const remove = (category: SensitiveCategory) =>
        setDeclarations(current => current.filter(d => d.category !== category));

    const editModal = modal.create<ISensitiveCategoryFormProps>(SensitiveCategoryForm, {
        onSubmit: (values, formikActions, close) => {
            saveSellerSensitiveCategory(sellerId, values.category, toDeclarationRequest(values))
                .then(saved => {
                    replace(saved);
                    close();
                })
                .catch(onSubmitError(formikActions));
        }
    });

    const rows: ICategoryRow[] = useMemo(() => {
        const byCategory = new Map(declarations.map(d => [d.category, d]));
        return sensitiveCategories.map(item => ({
            category: item.value,
            label: item.label,
            declaration: byCategory.get(item.value)
        }));
    }, [declarations]);

    const unanswered = rows.length - declarations.length;
    const canEdit = ability.canEditSensitiveCategories();
    const isPublisher = Boolean(ability.isSeller() || ability.isSelfServe());

    const description = isPublisher
        ? <>Tell us which sensitive ad categories you accept. We use your answers only to decide which{' '}
            <b>private deals</b> your inventory is eligible for. Categories you have not answered are left out of
            those deals. This does not change the open auction.{' '}
            {ability.isSelfServe()
                ? 'To block categories everywhere, use Blockings above.'
                : 'If you also want a category blocked in the open auction, ask your account manager.'}</>
        : <>Declares which sensitive ad categories this account accepts. Used only to decide which{' '}
            <b>private deals</b> the inventory is eligible for. It has no effect on the open auction &mdash; to block
            categories there, use Blockings above.</>;

    return <>
        <Title toolbar={
            <Button intent={Intent.SUCCESS} responsive
                disabled={isPending}
                leftIcon={<i className='fa fa-refresh'/>}
                onClick={load}>
                Refresh
            </Button>
        }>Sensitive Categories</Title>

        <p className={style.description}>{description}</p>

        {error && <div className={`${style.notice} ${style.error}`}>{error}</div>}

        {!error && unanswered > 0 && <div className={style.notice}>
            <span>
                {unanswered} {unanswered === 1 ? 'category is' : 'categories are'} not answered.
                Until answered, {unanswered === 1 ? 'it is' : 'they are'} treated as <b>not eligible</b> for private
                deals.
            </span>
            <span className={style.progress}>{declarations.length} of {rows.length} answered</span>
        </div>}

        <Table header
            records={rows}
            placeholder={isPending && <Progress/>}
            columns={[{
                field: 'label',
                header: 'Category'
            }, {
                field: 'declaration',
                header: 'Account',
                cell: (row: ICategoryRow) => <span className={`${style.status} ${statusClass(row.declaration?.decision)}`}>
                    {row.declaration ? getSensitiveCategoryDecisionLabel(row.declaration.decision) : 'Not answered'}
                </span>
            }, {
                header: 'App exceptions',
                cell: (row: ICategoryRow) => row.declaration?.exceptionBundles?.length
                    ? <TagsList values={row.declaration.exceptionBundles} maxItems={3} size="small"/>
                    : <span className={style.muted}>&mdash;</span>
            }, {
                header: 'Note',
                responsive: true,
                cellWrap: 'clamp-2',
                cell: (row: ICategoryRow) => row.declaration?.note?.trim()
                    ? row.declaration.note
                    : <span className={style.muted}>&mdash;</span>
            }, {
                header: 'Updated',
                hide: isPublisher,
                cell: (row: ICategoryRow) => row.declaration
                    ? <span className={style.updated}>
                        {formatDateTime(row.declaration.updatedAt)} &middot; {getEditorLabel(row.declaration.updatedByRole)}
                    </span>
                    : <span className={style.muted}>&mdash;</span>
            }, {
                small: true,
                hide: !canEdit,
                cell: (row: ICategoryRow) => <TableRowActions>
                    <Button
                        small
                        variant="menu"
                        onClick={() => editModal.show({category: row.category, declaration: row.declaration})}
                        leftIcon={<i className='fa fa-edit'/>}
                    >
                        {row.declaration ? 'Edit' : 'Answer'}
                    </Button>
                    {row.declaration && <Button
                        small
                        variant="menu"
                        intent={Intent.DANGER}
                        onClick={() => confirm(`Clear the answer for "${row.label}"?`, {
                            onOk: () => deleteSellerSensitiveCategory(sellerId, row.category)
                                .then(() => remove(row.category))
                                .catch(e => setError(getApiErrorMessage(e, 'Could not clear the answer'))),
                            icon: <LucideIcon icon={Trash2} size={20}/>,
                            intent: Intent.DANGER
                        })}
                        leftIcon={<i className='fa fa-trash'/>}
                    >
                        Clear
                    </Button>}
                </TableRowActions>
            }]}/>
    </>;
};
